import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { graphql } from 'gatsby';
import Layout from '../../layout/Layout';
import { getParagraph } from '../../components/componentLookup';
import { SEO } from '../../layout/SEO';

import {
    DataCards,
    DataCardsProps,
} from '../../components/DataCards/DataCards';
import { DataCardProps } from '../../components/DataCards/DataCard';
import { Hero } from '../../components/Hero/Hero';
import { getImage } from 'gatsby-plugin-image';
import {
    CustomerSummary,
    CustomerSummaryProps,
} from '../../components/CustomerSummary/CustomerSummary';
import { processMetatags } from '../../shared/metaTags';
import { makeAnalyticsCall } from '../../shared/analytics/callAnalytics';
import { PromoBarInputs } from '../../layout/Header/PromoBar/PromoBar';
import { EventName } from '../../models/GTMEventTypes';
import { processSchema } from '../../shared/Schema/Schema';

export default function CaseStudy({ data }) {
    const { nodeCaseStudyPage: page } = data;
    const { field_summary: summary } = page.relationships;
    const { field_customer: customer } = summary.relationships;
    const customSchemas = page.relationships?.field_schema;

    let promoBar: PromoBarInputs = null;
    if (page.relationships?.field_promo_bar) {
        promoBar = {
            text: page.relationships.field_promo_bar.field_content_text?.value,
            header: page.relationships.field_promo_bar.field_heading,
        };
    }

    const paragraphs = page.relationships.field_components.map((paragraph) => {
        if (paragraph.type === `paragraph__case_study_data_cards`) {
            const dataCardsProps: DataCardsProps = {
                cards: [],
                darkTheme: paragraph.field_dark_mode,
                customerName: customer?.title,
                embeddedCS: true,
            };
            for (const item of page.relationships.field_summary?.relationships
                .field_data) {
                const card: DataCardProps = {
                    title: item.field_heading,
                    value: item.field_subtitle,
                    showArrow: item.field_show_arrow,
                    footnoteIndex: item.field_footnote_index,
                };
                dataCardsProps.cards.push(card);
            }

            return (
                <DataCards
                    {...dataCardsProps}
                    key={summary?.field_subtitle ?? 0}
                />
            );
        } else if (
            paragraph.type === `paragraph__case_study_customer_summary`
        ) {
            const props: CustomerSummaryProps = {
                text: customer?.body?.value,
                headline: customer?.title,
                industry: customer?.relationships?.field_industry?.title,
                location: customer?.field_location,
                founded: customer?.field_founded,
                industryLink: `https://google.com`,
                theme: paragraph.field_theme,
                image: paragraph.relationships?.field_image
                    ? getImage(
                          paragraph.relationships.field_image.relationships
                              .field_media_image.localFile
                      )
                    : getImage(
                          customer?.relationships?.field_customer_image
                              ?.relationships?.field_media_image?.localFile
                      ),
                imageAlt: paragraph.relationships?.field_image
                    ? paragraph.relationships.field_image.field_media_image.alt
                    : customer?.relationships?.field_customer_image
                          ?.field_media_image?.alt,
            };
            return <CustomerSummary key={props.headline} {...props} />;
        } else {
            return getParagraph(paragraph);
        }
    });

    useEffect(() => {
        makeAnalyticsCall(EventName.PAGE_VIEW, {
            pageType: `Case Study`,
        });
        processSchema(customSchemas);
    }, []);

    return (
        <Layout promoBar={promoBar}>
            <Hero
                heading={summary.field_heading}
                subheading={summary.field_subtitle}
                cols={2}
                theme="dark"
                eyebrow={`${customer?.relationships?.field_industry?.title}`}
                taggedImage={{
                    image: getImage(
                        summary.relationships.field_image?.relationships
                            ?.field_media_image?.localFile
                    ),
                    imageAlt:
                        summary.relationships.field_image?.field_media_image
                            ?.alt,
                    bg: `white`,
                    placement: `right`,
                    tag: {
                        image: getImage(
                            customer?.relationships?.field_customer_image
                                ?.relationships?.taggedLogo?.localFile
                        ),
                    },
                }}
                align="right"
                callsToAction={summary.field_link}
            />
            {paragraphs}
        </Layout>
    );
}

export const Head = ({ data }) => {
    const { nodeCaseStudyPage: page } = data;
    return (
        <>
            <SEO location={page.fields.url}>
                {processMetatags(page.metatag)}
            </SEO>
        </>
    );
};

export const query = graphql`
    query ($id: String!) {
        nodeCaseStudyPage(id: { eq: $id }) {
            fields {
                url
            }
            title
            drupal_internal__nid
            drupal_internal__vid
            moderation_state
            metatag {
                tag
                attributes {
                    content
                    href
                    name
                    rel
                    property
                }
            }
            relationships {
                field_schema {
                    type: __typename
                    ...paragraph__custom_schema
                }
                field_promo_bar {
                    ...paragraph__promo_bar
                }
                field_summary {
                    field_heading
                    field_subtitle
                    field_link {
                        title
                        uri
                    }
                    relationships {
                        field_customer {
                            title
                            body {
                                value
                            }
                            field_founded
                            field_location
                            relationships {
                                field_industry {
                                    title
                                }
                                field_customer_image {
                                    relationships {
                                        taggedLogo: field_media_image {
                                            localFile {
                                                childImageSharp {
                                                    ...ImageLogoSettings
                                                }
                                            }
                                        }
                                        field_media_image {
                                            localFile {
                                                childImageSharp {
                                                    ...ImageConstrainedOneHalfSettings
                                                }
                                            }
                                        }
                                    }
                                    field_media_image {
                                        alt
                                    }
                                }
                            }
                        }
                        field_data {
                            field_heading
                            field_subtitle
                            field_show_arrow
                            field_footnote_index
                        }
                        field_image {
                            field_media_image {
                                alt
                            }
                            relationships {
                                field_media_image {
                                    localFile {
                                        childImageSharp {
                                            ...ImageFullWidthSettings
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
                field_components {
                    type: __typename
                    ...paragraph__article_inline_callout
                    ...paragraph__article_sources_list
                    ...paragraph__award_primary_link
                    ...paragraph__award_secondary_list
                    ...paragraph__award_tertiary_link
                    ...paragraph__case_study_metrics
                    ...paragraph__case_study_data_cards
                    ...paragraph__case_study_customer_summary
                    ...paragraph__components_anchor
                    ...paragraph__dynamic_case_study_preview
                    ...paragraph__embedded_image
                    ...paragraph__embedded_video
                    ...paragraph__external_form
                    ...paragraph__four_column
                    ...paragraph__header_body_collection
                    ...paragraph__infographic
                    ...paragraph__newsletter_signup
                    ...paragraph__quote
                    ...paragraph__recommended_articles
                    ...paragraph__single_column_text
                    ...paragraph__three_column
                    ...paragraph__three_column_narrow
                    ...paragraph__two_column
                    ...paragraph__two_column_form_text
                    ...paragraph__two_column_text_image
                    ...paragraph__two_column_text_media
                }
            }
        }
    }
`;
